'use client'

import { useEffect, useState, type CSSProperties } from 'react'
import Artwork from './Artwork'
import type { PlayerTrack } from '@/lib/player-context'

/**
 * Vue "pochette + CD" : la jaquette carrée de l'album. Au repos, la pochette est
 * FERMÉE (le disque est entièrement caché derrière). Quand la musique démarre, le
 * disque SORT un peu sur la droite et tourne. Cliquer sur la pochette lance /
 * met en pause la musique (le disque sort et se met à tourner).
 *
 * `variant` : couleur du disque, 'black' (défaut) ou 'white'. Si non fourni, on
 * lit le choix mémorisé (localStorage 'blc.vinylColor') et on réagit au live
 * toggle (événement 'blc-vinyl-color') — même système que VinylDisc.
 */
type Variant = 'black' | 'white'
type Props = {
  track: PlayerTrack | null | undefined
  playing: boolean
  size?: number // côté de la pochette
  variant?: Variant
  /** Si fourni, cliquer sur la pochette bascule play/pause. */
  onToggle?: () => void
}

function readSaved(): Variant {
  if (typeof localStorage === 'undefined') return 'black'
  try { return localStorage.getItem('blc.vinylColor') === 'white' ? 'white' : 'black' } catch { return 'black' }
}

export default function SleeveDisc({ track, playing, size = 320, variant, onToggle }: Props) {
  const [selfVar, setSelfVar] = useState<Variant>(variant ?? 'black')
  useEffect(() => {
    if (variant) return
    setSelfVar(readSaved())
    const upd = () => setSelfVar(readSaved())
    window.addEventListener('blc-vinyl-color', upd)
    window.addEventListener('storage', upd)
    return () => { window.removeEventListener('blc-vinyl-color', upd); window.removeEventListener('storage', upd) }
  }, [variant])
  const white = (variant ?? selfVar) === 'white'

  const cd = Math.round(size * 0.94)            // diamètre du vinyle
  // Repos : peek = 0 → disque totalement caché (pochette fermée). Lecture : il
  // dépasse « un peu » sur la droite.
  const peek = playing ? size * 0.42 : 0        // combien le vinyle dépasse à droite
  const totalW = size + peek
  const label = Math.round(cd * 0.42)           // étiquette centrale (jaquette)
  const hole = Math.max(8, cd * 0.035)

  const cdStyle: CSSProperties = {
    position: 'absolute',
    top: '50%',
    left: size - (cd - peek),
    width: cd,
    height: cd,
    marginTop: -cd / 2,
    borderRadius: '50%',
    // Matière du disque selon la couleur (identique au grand vinyle).
    background: white
      ? [
          'linear-gradient(135deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0) 28%, rgba(0,0,0,0.05) 72%, rgba(255,255,255,0.35) 100%)',
          'repeating-radial-gradient(circle at 50% 50%, #dcdce2 0px, #dcdce2 1px, #f6f6f9 2px, #dcdce2 3px)',
          'radial-gradient(circle at 50% 50%, #ffffff 0%, #ececf1 58%, #d9d9e0 100%)',
        ].join(',')
      : [
          'linear-gradient(135deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0) 30%, rgba(255,255,255,0) 70%, rgba(255,255,255,0.06) 100%)',
          'repeating-radial-gradient(circle at 50% 50%, #0b0b0f 0px, #0b0b0f 1px, #17171d 2px, #0b0b0f 3px)',
          'radial-gradient(circle at 50% 50%, #1a1a22 0%, #050507 60%, #000 100%)',
        ].join(','),
    boxShadow: white
      ? '0 0 0 1px rgba(0,0,0,0.10) inset, 0 12px 30px rgba(0,0,0,0.35), 0 0 26px rgb(var(--a1)/0.22)'
      : '0 0 0 1px rgba(255,255,255,0.06) inset, 0 12px 30px rgba(0,0,0,0.55), 0 0 26px rgb(var(--a1)/0.30)',
    transition: 'left .55s cubic-bezier(0.22,1,0.36,1), box-shadow .4s ease',
    animation: 'spinSlow 4s linear infinite',
    animationPlayState: playing ? 'running' : 'paused',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    willChange: 'transform',
  }

  return (
    <div style={{ position: 'relative', width: totalW, height: size }}>
      {/* Vinyle derrière la pochette, avec la jaquette collée au centre */}
      <div style={cdStyle}>
        <div
          style={{
            width: label, height: label, borderRadius: '50%', overflow: 'hidden',
            boxShadow: white
              ? '0 0 0 4px #f0f0f4, 0 0 0 5px rgba(0,0,0,0.12)'
              : '0 0 0 4px #0a0a0d, 0 0 0 5px rgba(255,255,255,0.12)',
            position: 'relative',
          }}
        >
          <Artwork track={track} size={label} rounded="rounded-full" className="w-full h-full" transition />
          {/* trou central */}
          <div
            style={{
              position: 'absolute', top: '50%', left: '50%',
              width: hole, height: hole, transform: 'translate(-50%,-50%)',
              borderRadius: '50%',
              background: white
                ? 'radial-gradient(circle, #d9d9e0 40%, #f6f6f9 100%)'
                : 'radial-gradient(circle, #000 40%, #222 100%)',
              boxShadow: white ? '0 0 0 2px rgba(0,0,0,0.18)' : '0 0 0 2px rgba(255,255,255,0.15)',
            }}
          />
        </div>
      </div>

      {/* Pochette (sleeve) carrée — par-dessus le CD. Cliquable → play/pause. */}
      <div
        onClick={onToggle ? (e) => { e.stopPropagation(); onToggle() } : undefined}
        role={onToggle ? 'button' : undefined}
        aria-label={onToggle ? (playing ? 'Mettre en pause' : 'Lancer la musique') : undefined}
        title={onToggle ? (playing ? 'Pause' : 'Lancer la musique') : undefined}
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: size,
          height: size,
          borderRadius: 14,
          overflow: 'hidden',
          zIndex: 2,
          cursor: onToggle ? 'pointer' : 'default',
          boxShadow:
            '0 22px 50px rgba(0,0,0,0.6), 0 0 40px rgb(var(--a1)/0.22), inset 0 0 0 1px rgba(255,255,255,0.06)',
        }}
      >
        <Artwork track={track} size={size} rounded="rounded-none" className="w-full h-full" />
        {/* liseré "ouverture" de la pochette à droite */}
        <div
          style={{
            position: 'absolute', top: 0, right: 0, width: 10, height: '100%',
            background: 'linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.45) 100%)',
            pointerEvents: 'none',
          }}
        />
      </div>
    </div>
  )
}
